import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ChevronRight, Menu, Search, X, Sparkles, Calendar } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import CalendlyModal from "@/components/CalendlyModal";

const NAV_LINKS = [
  { label: "Industries", to: "/industries" },
  { label: "Trackers", to: "/trackers" },
  { label: "Modules", to: "/modules" },
  { label: "Frameworks", to: "/frameworks" },
  { label: "IP Services", to: "/services/ip" },
  { label: "Briefings", to: "/briefings" },
  { label: "Firm", to: "/firm" },
];

export default function TopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim().length >= 2) navigate(`/search?q=${encodeURIComponent(search.trim())}`);
    setSearchOpen(false);
  };

  return (
    <>
      <CalendlyModal open={calOpen} onClose={() => setCalOpen(false)} />

      <header className="sticky top-0 z-40 bg-white border-b border-[var(--border)]" data-testid="topbar">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3 sm:gap-6">
          <Link to="/" data-testid="logo-link" className="flex-shrink-0 group">
            <Logo size="md" />
          </Link>

          <nav className="hidden xl:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--navy)] hover:text-[var(--gold)] transition-colors"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/luminous"
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--navy)] hover:text-[var(--gold)] transition-colors flex items-center gap-1.5 whitespace-nowrap"
              data-testid="nav-luminous"
            >
              <Sparkles size={11} className="text-[var(--gold)]" />
              Luminous AI
            </Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="hidden sm:flex items-center text-[var(--navy)] hover:text-[var(--gold)] transition-colors"
              data-testid="search-toggle"
              aria-label="Search"
            >
              <Search size={18} />
            </button>
            {user && typeof user === "object" ? (
              <div className="hidden md:flex items-center gap-3">
                {user.role === "admin" && (
                  <Link to="/admin" className="btn-ghost" data-testid="admin-link">Admin</Link>
                )}
                <Link to="/dashboard" className="btn-ghost" data-testid="dashboard-link">
                  {(user.name || "Member").split(" ")[0]}
                </Link>
                <button onClick={logout} className="btn-ghost" data-testid="logout-btn">
                  Sign out
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-3">
                <Link to="/login" className="btn-ghost" data-testid="login-link">Sign in</Link>
              </div>
            )}
            <button
              onClick={() => setCalOpen(true)}
              className="btn-primary text-[10px] sm:text-[11px] px-3 sm:px-5"
              data-testid="schedule-session-btn"
            >
              <Calendar size={13} className="hidden sm:inline" />
              <span className="hidden md:inline">Schedule Strategy Session</span>
              <span className="md:hidden">Book a call</span>
              <ChevronRight size={14} />
            </button>
            <button
              className="xl:hidden text-[var(--navy)]"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-[var(--border)] bg-white">
            <form onSubmit={onSearch} className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-3">
              <Search size={16} className="text-[var(--slate-light)]" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search industries, trackers, frameworks…"
                className="flex-1 outline-none font-sans text-[15px] placeholder:text-[var(--slate-light)]"
                data-testid="search-input"
              />
              <button type="submit" className="btn-secondary" data-testid="search-submit">Search</button>
            </form>
          </div>
        )}

        {open && (
          <div className="xl:hidden border-t border-[var(--navy)] bg-white px-6 py-6 space-y-3" data-testid="mobile-menu">
            {NAV_LINKS.concat([
              { label: "Luminous AI", to: "/luminous" },
              { label: "Contact", to: "/contact" },
            ]).map((l) => (
              <Link key={l.to} to={l.to} className="block font-mono text-xs uppercase tracking-[0.2em] py-1 text-[var(--navy)]" onClick={() => setOpen(false)}>
                {l.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-[var(--border)] space-y-3">
              {!user || user === false ? (
                <Link to="/login" className="block font-mono text-xs uppercase tracking-[0.2em] text-[var(--navy)]" onClick={() => setOpen(false)}>Sign in</Link>
              ) : (
                <>
                  {user.role === "admin" && <Link to="/admin" className="block font-mono text-xs uppercase text-[var(--navy)]" onClick={() => setOpen(false)}>Admin</Link>}
                  <Link to="/dashboard" className="block font-mono text-xs uppercase text-[var(--navy)]" onClick={() => setOpen(false)}>Dashboard</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="font-mono text-xs uppercase text-[var(--navy)]">Sign out</button>
                </>
              )}
              <button
                onClick={() => { setCalOpen(true); setOpen(false); }}
                className="btn-primary w-full justify-center"
                data-testid="mobile-schedule-session-btn"
              >
                <Calendar size={13} /> Schedule Strategy Session
              </button>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
